import classNames from 'classnames';
import DropDownPicker, { IDropDownPickerProps } from 'components/foundation/pickers/DropDownPicker/DropDownPicker';
import SimplePickerRow from 'components/foundation/pickers/SimplePickerRow/SimplePickerRow';
import { useMemo } from 'react';
import { ClassName } from 'types/ClassName';
import { Nullable } from 'types/util/Nullable';
import { t } from 'utils/localize';

export type TGroupPickerItem = { id: string; groupName: string };

export interface IGroupPickerProps
  extends Omit<IDropDownPickerProps<TGroupPickerItem, string>, 'data' | 'renderRow' | 'valueTemplate' | 'onSelect'> {
  data: Nullable<TGroupPickerItem[]>;
  currentGroup: Nullable<string>;
  includeNoneOption?: boolean;
  onSelect: (groupId: Nullable<string>) => void;
  className?: ClassName;
}

export type TUseGroupDropDownPickerPayload<T extends TGroupPickerItem> = {
  data: Nullable<T[]>;
  value: Nullable<string>;
  includeNoneOption: boolean;
  selectedGroupName: Nullable<string>;
  searchValue: string | undefined;
  onSelect: (group: Nullable<string>) => void;
};

export function useGroupDropDownPicker<T extends TGroupPickerItem>({
  data,
  value,
  includeNoneOption,
  searchValue,
  selectedGroupName,
  onSelect: handleSelect,
}: TUseGroupDropDownPickerPayload<T>) {
  const withNoneOption: TGroupPickerItem[] = useMemo(() => {
    // NOTE: Don't show `No Group` option if data is null to avoid confusion with loading state
    if (!includeNoneOption || data === null) {
      return data ?? [];
    }

    const nonNullData = data ?? [];
    const noneOption = { id: 'none', groupName: t('No Group') };
    return [...nonNullData, noneOption];
  }, [data, includeNoneOption]);

  function renderRow(group: TGroupPickerItem) {
    return <SimplePickerRow value={group.groupName} searchValue={searchValue} />;
  }

  function renderValueTemplate() {
    if (selectedGroupName) {
      return <>{selectedGroupName}</>;
    } else if (value === 'none' && includeNoneOption) {
      return <>{t('No Group')}</>;
    }

    return <></>;
  }

  function onSelect(group: Nullable<TGroupPickerItem>) {
    handleSelect(group?.id ?? null);
  }

  return { renderRow, data: withNoneOption, valueTemplate: renderValueTemplate(), onSelect };
}

export default function GroupPicker({
  value,
  data,
  currentGroup,
  includeNoneOption,
  onSelect: handleSelect,
  className,
  ...pickerProps
}: IGroupPickerProps) {
  const classes = classNames('group-picker', className);

  return (
    <DropDownPicker
      {...pickerProps}
      {...useGroupDropDownPicker({
        data,
        value,
        includeNoneOption: !!includeNoneOption,
        selectedGroupName: currentGroup,
        searchValue: undefined,
        onSelect: handleSelect,
      })}
      value={value}
      className={classes}
    />
  );
}
